import { memo, useCallback, useEffect, useRef } from 'react'
import { useRootContext } from '@/app/services/RootProvider/RootProvider'
import { getUniqueId } from '@/lib/util/uniq'
import Body from './components/Body/Body'
import Container from './components/Container/Container'
import Header from './components/Header/Header'
import Portal from './components/Portal/Portal'
import Wrapper from './components/Wrapper/Wrapper'
import { useModalContext } from './provider/ModalProvider'

/**
 * The `Modal` component
 * @returns {React.ReactElement} the element
 */
function ModalComponent () {
  const {
    isModalActive,
    isModalHidden,
    isModalOpen,
    onAction,
    preventExit,
    setModalRef,
    shouldRender,
    size,
    style
  } = useModalContext()
  const { userActivityStatus } = useRootContext()
  const ariaIdLabel = useRef(`modal-${getUniqueId()}-label`)
  const ref = useRef()

  /**
   * Handle close (via the wrapper)
   */
  const handleClose = useCallback(() => {
    // Dispatch the close action
    onAction('close')
  }, [])

  useEffect(() => {
    setModalRef(ref)
  }, [])

  // Lock body scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      // Save current body overflow style
      const originalOverflow = document.body.style.overflow
      const originalPosition = document.body.style.position
      const originalTop = document.body.style.top
      const scrollY = window.scrollY

      // Lock body scroll
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'

      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalOverflow
        document.body.style.position = originalPosition
        document.body.style.top = originalTop
        window.scrollTo(0, scrollY)
      }
    }
  }, [isModalOpen])

  return (
    <Wrapper
      ref={ref}
      ariaIdLabel={ariaIdLabel.current}
      isOpen={isModalOpen}
      isHidden={isModalHidden}
      isActive={isModalActive}
      onClose={handleClose}
      preventExit={preventExit || !!userActivityStatus}
      shouldRender={shouldRender}
      size={size}
      style={style}
    />
  )
}

// Memoize
const Modal = memo(ModalComponent)

Modal.Portal = Portal
Modal.Header = Header
Modal.Container = Container
Modal.Body = Body

// Export
export default Modal
